/** Lê o conteúdo textual de um arquivo SVG. */
export async function readSvgFile(file: File): Promise<string> {
  return file.text();
}

const PAINTABLE_SELECTOR =
  'path, circle, rect, ellipse, polygon, polyline, line, g, use, text';

function parseSvgDocument(svg: string): SVGSVGElement | null {
  try {
    const normalized = svg.trim().replace(/<\?xml[^?]*\?>/i, '');
    const doc = new DOMParser().parseFromString(normalized, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return null;
    return doc.documentElement as unknown as SVGSVGElement;
  } catch {
    return null;
  }
}

/** Substitui cores em blocos <style> embutidos (ex.: export Adobe Illustrator). */
function recolorEmbeddedStyles(root: SVGSVGElement, color: string): void {
  root.querySelectorAll('style').forEach((styleEl) => {
    styleEl.textContent = (styleEl.textContent ?? '')
      .replace(/fill\s*:\s*[^;}\s]+/gi, `fill: ${color}`)
      .replace(/stroke\s*:\s*[^;}\s]+/gi, `stroke: ${color}`);
  });
}

function isStrokeOnlyElement(el: Element): boolean {
  const fill = el.getAttribute('fill');
  const stroke = el.getAttribute('stroke');
  const style = el.getAttribute('style') ?? '';

  if (fill === 'none') return Boolean(stroke && stroke !== 'none');
  if (stroke && stroke !== 'none' && !fill) {
    if (/fill\s*:\s*none/i.test(style)) return true;
    if (!/fill\s*:/i.test(style)) return true;
  }
  return false;
}

/** Força cor no elemento, removendo classes CSS que sobrescrevem atributos SVG. */
function forcePaint(el: Element, color: string): void {
  el.removeAttribute('class');

  const strokeOnly = isStrokeOnlyElement(el);

  if (strokeOnly) {
    el.setAttribute('fill', 'none');
    el.setAttribute('stroke', color);
    el.setAttribute('style', `fill:none!important;stroke:${color}!important`);
    return;
  }

  el.setAttribute('fill', color);
  el.removeAttribute('stroke');
  el.setAttribute('style', `fill:${color}!important`);
}

/** Aplica cor a todos os elementos pintáveis do SVG. */
export function applySvgColor(svg: string, color: string): string {
  const root = parseSvgDocument(svg);
  if (!root) return applySvgColorRegex(svg, color);

  if (!root.getAttribute('xmlns')) {
    root.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  }

  recolorEmbeddedStyles(root, color);

  const elements = root.querySelectorAll(PAINTABLE_SELECTOR);
  if (elements.length > 0) {
    elements.forEach((el) => forcePaint(el, color));
  } else {
    forcePaint(root, color);
  }

  return new XMLSerializer().serializeToString(root);
}

/** Fallback sem DOMParser (regex). */
function applySvgColorRegex(svg: string, color: string): string {
  let result = svg.trim().replace(/<\?xml[^?]*\?>/i, '');

  if (!result.includes('xmlns')) {
    result = result.replace(/^<svg/i, '<svg xmlns="http://www.w3.org/2000/svg"');
  }

  result = result.replace(/fill\s*:\s*[^;}\s]+/gi, `fill: ${color}`);
  result = result.replace(/stroke\s*:\s*[^;}\s]+/gi, `stroke: ${color}`);

  result = result.replace(/\sclass\s*=\s*"[^"]*"/gi, '');
  result = result.replace(/\sclass\s*=\s*'[^']*'/gi, '');

  result = result.replace(/\sfill\s*=\s*"(?!none)[^"]*"/gi, '');
  result = result.replace(/\sfill\s*=\s*'(?!none)[^']*'/gi, '');
  result = result.replace(/\sstroke\s*=\s*"(?!none)[^"]*"/gi, '');
  result = result.replace(/\sstroke\s*=\s*'(?!none)[^']*'/gi, '');

  result = result.replace(
    /<(path|circle|rect|ellipse|polygon|polyline|line)(\s[^>]*?)(\/?)>/gi,
    (_match, tag, attrs, selfClose) => {
      if (/fill\s*=\s*["']none["']/i.test(attrs)) {
        return `<${tag}${attrs} stroke="${color}" style="stroke:${color}!important;fill:none!important"${selfClose ? '/' : ''}>`;
      }
      return `<${tag}${attrs} fill="${color}" style="fill:${color}!important"${selfClose ? '/' : ''}>`;
    },
  );

  return result;
}

/** Converte SVG em data URL para preview (compatível com CSP de produção). */
export function svgToDataUrl(svg: string): string {
  const encoded = encodeURIComponent(svg).replace(/%0A/g, '').replace(/%20/g, ' ');
  return `data:image/svg+xml,${encoded}`;
}

/** Prepara o arquivo SVG com a cor escolhida antes do upload. */
export async function prepareFabIconFile(
  file: File | null,
  iconColor: string,
  fallbackSvg: string,
): Promise<File> {
  const raw = file ? await readSvgFile(file) : fallbackSvg;
  const colored = applySvgColor(raw, iconColor);
  return new File([colored], file?.name ?? 'icon.svg', { type: 'image/svg+xml' });
}

/** SVG padrão quando o usuário não envia ícone (cor aplicada via applySvgColor). */
export function defaultFabIconSvg(): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
    <path d="M12 8v8M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  </svg>`;
}

/** Ícones na S3 privada passam pelo BFF; catálogo público Fixe usa URL direta. */
const FAB_ICON_S3_PROXY_PATTERN = /amazonaws\.com\/fab-buttons\//i;

/** URL para exibir ícone FAB (proxy só quando necessário). */
export function resolveFabIconSrc(iconUrl: string): string {
  if (iconUrl.startsWith('data:')) return iconUrl;
  if (FAB_ICON_S3_PROXY_PATTERN.test(iconUrl)) {
    return `/bff/assets/fab-icon?url=${encodeURIComponent(iconUrl)}`;
  }
  return iconUrl;
}

/** @deprecated Preferir resolveFabIconSrc */
export function fabIconProxyUrl(iconUrl: string): string {
  return resolveFabIconSrc(iconUrl);
}
